// src/hooks/useApi.js
import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Generic API hook for calling backend.
 * - Handles loading, error, abort
 * - Supports lazy mode and manual re-fetch via fetchData
 */
export function useApi(initialUrl, lazy = false) {
  const [url, setUrl] = useState(initialUrl);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!lazy);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const controllerRef = useRef(null);

  const fetchData = useCallback(
    async (overrideUrl) => {
      const targetUrl = overrideUrl || url;
      if (!targetUrl) return;

      // Cancel any in-flight request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      const controller = new AbortController();
      controllerRef.current = controller;
      const { signal } = controller;

      setLoading(true);
      setError(null);
      setErrorStatus(null);

      try {
        const res = await fetch(targetUrl, { signal });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          const error = new Error(
            `API Error ${res.status}: ${text || res.statusText || 'Unknown error'}`
          );
          error.status = res.status;
          throw error;
        }

        const json = await res.json();
        setData(json);
      } catch (err) {
        if (err.name === 'AbortError') {
          // Request was canceled — don’t treat as an error
          return;
        }
        console.error('Error in useApi:', err);
        setError(err.message || 'Unknown error');
        setErrorStatus(err.status || null);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [url]
  );

  // Keep internal URL in sync with prop
  useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl]);

  // Auto-fetch unless lazy
  useEffect(() => {
    if (!lazy && url) {
      fetchData(url);
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [url, lazy, fetchData]);

  return { data, loading, error, errorStatus, fetchData };
}
